/**
 * MetricCard Component
 *
 * Reusable card component for displaying metric values with labels
 * Extracted from PoolReportsSummaryPage for reusability
 */

import { ReactNode } from 'react';
import clsx from 'clsx';

export interface MetricCardProps {
  label: string;
  value: string | number | ReactNode;
  className?: string;
  valueClassName?: string;
  labelClassName?: string;
  variant?: 'default' | 'primary' | 'accent';
  size?: 'sm' | 'md' | 'lg';
}

const variantStyles = {
  default: 'bg-white dark:bg-dark-800 border-gray-200 dark:border-dark-700',
  primary: 'bg-primary-50 dark:bg-primary-900/20 border-primary-200 dark:border-primary-800',
  accent:
    'bg-gradient-to-br from-primary-50 to-blue-50 dark:from-primary-900/20 dark:to-blue-900/20 border-primary-200 dark:border-primary-800',
};

const sizeStyles = {
  sm: {
    container: 'p-3',
    label: 'text-xs',
    value: 'text-base',
  },
  md: {
    container: 'p-3 sm:p-4',
    label: 'text-xs sm:text-sm',
    value: 'text-base sm:text-lg',
  },
  lg: {
    container: 'p-6',
    label: 'text-base',
    value: 'text-2xl',
  },
};

export function MetricCard({
  label,
  value,
  className,
  valueClassName,
  labelClassName,
  variant = 'default',
  size = 'md',
}: MetricCardProps) {
  const sizeStyle = sizeStyles[size];

  return (
    <div
      className={clsx(
        'rounded-lg border transition-all duration-200',
        variantStyles[variant],
        sizeStyle.container,
        className
      )}
    >
      <div
        className={clsx(
          'mb-1 font-medium text-gray-500 dark:text-gray-400',
          sizeStyle.label,
          labelClassName
        )}
      >
        {label}
      </div>
      <div
        className={clsx(
          'font-semibold tabular-nums text-gray-900 dark:text-white',
          sizeStyle.value,
          valueClassName
        )}
      >
        {value}
      </div>
    </div>
  );
}
